import { X } from "lucide-react";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import { useComponentDefaults } from "../../Provider";
import { keys } from "../../utils/a11y";
import { clsx } from "../../utils/clsx";
import Button from "../Button/Button";
import s from "./Modal.module.css";

let modalScrollLockCount = 0;
let initialBodyOverflow;

const lockBodyScroll = () => {
  if (typeof document === "undefined" || typeof window === "undefined") return () => {};
  if (modalScrollLockCount === 0) {
    initialBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  modalScrollLockCount += 1;
  return () => {
    if (typeof document === "undefined" || typeof window === "undefined") return;
    modalScrollLockCount = Math.max(0, modalScrollLockCount - 1);
    if (modalScrollLockCount === 0) {
      document.body.style.overflow = initialBodyOverflow ?? "";
      initialBodyOverflow = undefined;
    }
  };
};

/* AntD-like Modal props surface (subset implemented) */
export default function Modal(props) {
  const merged = useComponentDefaults('Modal', props, {
    open: false,
    cancelText: 'Cancel',
    okText: 'OK',
    okType: 'primary',
    confirmLoading: false,
    centered: false,
    closable: true,
    closeIcon: (
      <Button
        size="sm"
        shape="circle"
        variant="secondary"
        aria-label="Close"
        icon={<X size={14} aria-hidden />}
      />
    ),
    keyboard: true,
    mask: true,
    maskClosable: true,
    closeOnOutsideClick: true,
    fullscreen: false, // false | true | 'mobile' | 'desktop'
    manageHistory: true,
    destroyOnHidden: false,
    forceRender: false,
    focusTriggerAfterClose: true,
    getContainer: () => (typeof document !== 'undefined' ? document.body : null),
    width: 700,
    zIndex: 1000,
    loading: false,
    header: undefined,
    ariaLabel: undefined,
    ariaLabelledBy: undefined,
  });
  const {
    open = false,
    onClose,
    onCancel,
    onOk,
    afterClose,
    afterOpenChange,
    title,
    className,
    wrapClassName,
    classNames,
    styles,
    header: headerProp,
    footer,
    cancelText = 'Cancel',
    okText = 'OK',
    okType = 'primary',
    cancelButtonProps,
    okButtonProps,
    confirmLoading = false,
    centered = false,
    closable = true,
    closeIcon = (
      <Button
        size="sm"
        shape="circle"
        variant="secondary"
        aria-label="Close"
        icon={<X size={14} aria-hidden />}
      />
    ),
    keyboard = true,
    mask = true,
    maskClosable = true,
    closeOnOutsideClick = true,
    fullscreen = false,
    manageHistory = true,
    destroyOnHidden = false,
    forceRender = false,
    focusTriggerAfterClose = true,
    getContainer = () => (typeof document !== 'undefined' ? document.body : null),
    modalRender,
    width = 700,
    zIndex = 1000,
    style,
    loading = false,
    children,
    ariaLabel,
    ariaLabelledBy,
  } = merged;
  const id = useId();
  const ref = useRef(null);
  const prevOpenRef = useRef(open);
  const lastOpenRef = useRef(open);
  const overlayRef = useRef(null);
  const closeHandlersRef = useRef({ onClose, onCancel });
  const [mounted, setMounted] = useState(open || forceRender);

  closeHandlersRef.current = { onClose, onCancel };

  // Mount when opened (unmount on close handled after transition)
  useEffect(() => { if (open) setMounted(true); }, [open]);

  // Focus trap + scroll lock when open
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement;
    const onKey = (e) => {
      if (keyboard && e.key === keys.ESC) {
        const closeHandler = closeHandlersRef.current.onClose || closeHandlersRef.current.onCancel;
        closeHandler?.(e);
      }
      if (e.key === keys.TAB && ref.current) {
        const focusable = ref.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    const unlockBodyScroll = lockBodyScroll();
    ref.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      unlockBodyScroll();
      if (focusTriggerAfterClose) previouslyFocused?.focus();
    };
  }, [open, keyboard, focusTriggerAfterClose]);

  // afterOpenChange fires on state toggle (immediately)
  useEffect(() => {
    if (prevOpenRef.current !== open) {
      afterOpenChange && afterOpenChange(open);
      prevOpenRef.current = open;
    }
  }, [open, afterOpenChange]);

  // When closing, wait for transition end then call afterClose and possibly unmount
  useEffect(() => {
    if (lastOpenRef.current && !open) {
      const node = overlayRef.current;
      let done = false;
      const finish = () => {
        if (done) return; done = true;
        afterClose && afterClose();
        if (destroyOnHidden && !forceRender) setMounted(false);
      };
      if (node) {
        const onEnd = (e) => {
          if (e.target === node && e.propertyName === 'opacity') {
            node.removeEventListener('transitionend', onEnd);
            finish();
          }
        };
        node.addEventListener('transitionend', onEnd);
        const t = setTimeout(finish, 300);
        return () => { node.removeEventListener('transitionend', onEnd); clearTimeout(t); };
      } else {
        finish();
      }
    }
    lastOpenRef.current = open;
  }, [open, afterClose, destroyOnHidden, forceRender]);

  // After close: if we pushed history and close wasn't from back, consume one history step
  useEffect(() => {
    if (manageHistory && !open && historyPushedRef?.current) {
      if (!handlingPopRef?.current) {
        try { window.history.back(); } catch {}
      }
      historyPushedRef.current = false;
      handlingPopRef.current = false;
    }
  }, [manageHistory, open]);

  const container = useMemo(() => {
    if (getContainer === false) return null;
    if (typeof getContainer === 'function') return getContainer();
    if (typeof getContainer === 'string' && typeof document !== 'undefined') return document.querySelector(getContainer);
    return getContainer || (typeof document !== 'undefined' ? document.body : null);
  }, [getContainer]);

  // History handling: in fullscreen, back button should close modal instead of navigating
  const isFullscreenActive = useMemo(() => {
    if (!fullscreen) return false;
    if (fullscreen === true) return true;
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    if (fullscreen === 'mobile') return width < 900;
    if (fullscreen === 'desktop') return width >= 900;
    return false;
  }, [fullscreen]);
  const historyPushedRef = useRef(false);
  const handlingPopRef = useRef(false);
  useEffect(() => {
    if (!manageHistory || !open || !isFullscreenActive) return;
    try {
      window.history.pushState({ __dsModal: true }, "");
      historyPushedRef.current = true;
    } catch {}
    const onPop = (e) => {
      if (e.state && e.state.__dsModal) {
        handlingPopRef.current = true;
        const closeHandler = closeHandlersRef.current.onClose || closeHandlersRef.current.onCancel;
        closeHandler?.(e);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      handlingPopRef.current = false;
    };
  }, [manageHistory, open, isFullscreenActive]);

  const handleMaskClick = (e) => {
    if (!mask || !maskClosable || !closeOnOutsideClick) return;
    (onClose || onCancel) && (onClose || onCancel)(e);
  };

  if (!mounted && !open && !forceRender) return null;

  const headerNode = headerProp !== undefined
    ? headerProp
    : (
      <div className={clsx(s.header, classNames?.header)} style={styles?.header}>
        {title ? (
          <h2 id={id} className={clsx(s.title, classNames?.title)} style={styles?.title}>
            {title}
          </h2>
        ) : <span />}
        {closable && closeIcon !== null && closeIcon !== false ? (
          React.isValidElement(closeIcon)
            ? React.cloneElement(closeIcon, {
                onClick: (e) => {
                  if (typeof closeIcon.props.onClick === 'function') closeIcon.props.onClick(e);
                  if (!e.defaultPrevented) (onClose || onCancel)?.(e);
                },
                // Do not force our close styles on custom element to preserve shapes (e.g., circle)
                className: clsx(classNames?.close, closeIcon.props.className),
              })
            : (
              <button
                type="button"
                className={clsx(s.close, classNames?.close)}
                style={styles?.close}
                onClick={(e) => (onClose || onCancel)?.(e)}
                aria-label="Close"
              >
                {closeIcon}
              </button>
            )
        ) : null}
      </div>
    );

  const OkBtn = (btnProps) => (
    <Button
      type={okType || "primary"}
      loading={confirmLoading}
      {...okButtonProps}
      {...btnProps}
      onClick={(e) => (btnProps.onClick || onOk)?.(e)}
    >
      {okText}
    </Button>
  );
  const CancelBtn = (btnProps) => (
    <Button variant="outlined" color="default" {...cancelButtonProps} {...btnProps} onClick={(e) => (btnProps.onClick || onCancel || onClose)?.(e)}>
      {cancelText}
    </Button>
  );

  const originFooter = (
    <div className={clsx(s.footer, classNames?.footer)} style={styles?.footer}>
      <CancelBtn />
      <OkBtn />
    </div>
  );

  let footerNode = originFooter;
  if (footer === null) footerNode = null;
  else if (typeof footer === 'function') footerNode = footer(originFooter, { OkBtn, CancelBtn });
  else if (footer) footerNode = <div className={clsx(s.footer, classNames?.footer)} style={styles?.footer}>{footer}</div>;

  const bodyNode = (
    <div className={clsx(s.body, classNames?.body)} style={styles?.body}>
      {children}
    </div>
  );

  const fsAttr = fullscreen === true ? 'always' : (fullscreen === 'mobile' ? 'mobile' : (fullscreen === 'desktop' ? 'desktop' : 'off'));
  const widthStyle = fullscreen ? undefined : width;
  const computedLabelledBy = ariaLabelledBy ?? (headerProp === undefined && title ? id : undefined);

  const modalNode = (
    <div
      ref={ref}
      tabIndex={-1}
      className={clsx(
        s.modal,
        className,
        classNames?.modal,
        fullscreen === true && s.fullscreen,
        fullscreen === 'mobile' && s.fullscreenMobile,
        fullscreen === 'desktop' && s.fullscreenDesktop,
      )}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={computedLabelledBy}
      onClick={(e) => e.stopPropagation()}
      style={{ ...(widthStyle != null ? { width: widthStyle } : null), ...style, ...styles?.modal }}
    >
      {headerNode}
      {bodyNode}
      {footerNode}
    </div>
  );

  const wrappedModal = modalRender ? modalRender(modalNode) : modalNode;
  const topOffset = centered ? '0px' : 'calc((var(--space-6) * 2) + var(--space-4))';
  const overlayStyle = {
    zIndex,
    alignItems: centered ? 'center' : 'flex-start',
    paddingTop: centered ? undefined : topOffset,
    ...(mask ? null : { background: 'transparent' }),
    '--ds-modal-top-offset': topOffset,
    ...styles?.overlay,
  };

  const overlay = (
    <div
      ref={overlayRef}
      className={clsx(s.overlay, wrapClassName, classNames?.overlay)}
      style={overlayStyle}
      onClick={handleMaskClick}
      data-open={open ? 'true' : 'false'}
      data-fs={fsAttr}
    >
      {wrappedModal}
    </div>
  );

  return container ? createPortal(overlay, container) : overlay;
}

const defaultConfirmConfig = {
  icon: null,
  okText: 'OK',
  cancelText: 'Cancel',
  okType: 'primary',
  okCancel: true,
  centered: true,
  closable: false,
  maskClosable: false,
  closeOnOutsideClick: true,
  width: 416,
};

const activeConfirmModals = new Set();
let confirmIdCounter = 0;

function confirm(config = {}) {
  if (typeof document === 'undefined') {
    return {
      destroy: () => {},
      update: () => {},
    };
  }

  const container = document.createElement('div');
  document.body.appendChild(container);
  const root = createRoot(container);

  let currentConfig = {
    ...defaultConfirmConfig,
    ...config,
    open: true,
  };

  function destroy() {
    activeConfirmModals.delete(close);
    root.unmount();
    // Defensive: avoid crashing if the container was already removed by React or the browser
    try {
      const parent = container.parentNode;
      if (parent && parent.contains(container)) {
        parent.removeChild(container);
      }
    } catch (err) {
      console.warn('[Modal.confirm] Failed to remove container', err);
    }
  }

  function close(triggerCancel = false) {
    const { afterClose } = currentConfig;
    currentConfig = {
      ...currentConfig,
      open: false,
      afterClose: () => {
        afterClose?.();
        destroy();
      },
    };
    render(currentConfig);
    if (triggerCancel) {
      // no-op; kept for signature parity with Ant Design
    }
  }

  function handleCancel(e) {
    const { onCancel } = currentConfig;
    if (!onCancel) {
      close(true);
      return;
    }
    let result;
    try {
      result = onCancel(e);
    } catch (err) {
      console.error(err);
      close(true);
      return;
    }

    if (result === false) return;

    if (result && typeof result.then === 'function') {
      result
        .then(() => close(true))
        .catch((err) => {
          console.error(err);
        });
      return;
    }

    close(true);
  }

  function handleOk(e) {
    const { onOk } = currentConfig;
    if (!onOk) {
      close(false);
      return;
    }
    let result;
    try {
      result = onOk(e);
    } catch (err) {
      console.error(err);
      close(false);
      return;
    }

    if (result === false) return;

    if (result && typeof result.then === 'function') {
      currentConfig = { ...currentConfig, confirmLoading: true };
      render(currentConfig);
      result
        .then(() => close(false))
        .catch((err) => {
          console.error(err);
          currentConfig = { ...currentConfig, confirmLoading: false };
          render(currentConfig);
        });
      return;
    }

    close(false);
  }

  function render(props) {
    const {
      icon,
      content,
      children,
      okCancel = true,
      footer,
      maskClosable,
      closable,
      closeOnOutsideClick,
      width,
      title,
      classNames: confirmClassNames,
      styles: confirmStyles,
      open: confirmOpen,
      ...rest
    } = props;

    const headingId = title ? `ds-modal-confirm-title-${++confirmIdCounter}` : undefined;
    const bodyContent = content ?? children ?? null;
    const hasHeading = Boolean(icon || title);

    const body = (
      <div className={s.confirmBodyContent}>
        {hasHeading ? (
          <div className={s.confirmHeading}>
            {icon ? <span className={s.confirmIcon}>{icon}</span> : null}
            {title ? (
              <h2 id={headingId} className={s.confirmTitle}>
                {title}
              </h2>
            ) : null}
          </div>
        ) : null}
        {bodyContent ? <div className={s.confirmContent}>{bodyContent}</div> : null}
      </div>
    );

    const mergedClassNames = {
      ...confirmClassNames,
      body: clsx(s.confirmBody, confirmClassNames?.body),
      footer: clsx(s.confirmFooter, confirmClassNames?.footer),
    };

    const footerProp =
      footer !== undefined
        ? footer
        : okCancel
          ? undefined
          : (_originalFooter, { OkBtn }) => <OkBtn />;

    root.render(
      <Modal
        {...rest}
        open={confirmOpen}
        onCancel={handleCancel}
        onClose={handleCancel}
        onOk={handleOk}
        footer={footerProp}
        maskClosable={maskClosable ?? defaultConfirmConfig.maskClosable}
        closeOnOutsideClick={closeOnOutsideClick ?? defaultConfirmConfig.closeOnOutsideClick}
        closable={closable ?? defaultConfirmConfig.closable}
        centered={props.centered ?? defaultConfirmConfig.centered}
        width={width ?? defaultConfirmConfig.width}
        title={null}
        header={null}
        classNames={mergedClassNames}
        styles={confirmStyles}
        ariaLabelledBy={headingId}
      >
        {body}
      </Modal>
    );
  }

  render(currentConfig);
  activeConfirmModals.add(close);

  return {
    destroy: () => close(true),
    update: (updates = {}) => {
      currentConfig = { ...currentConfig, ...updates };
      render(currentConfig);
    },
  };
}

confirm.destroyAll = () => {
  Array.from(activeConfirmModals).forEach((close) => {
    try {
      close(true);
    } catch (err) {
      console.error(err);
    }
  });
  activeConfirmModals.clear();
};

Modal.confirm = confirm;
Modal.destroyAll = () => confirm.destroyAll();

export { confirm };
